<template>
  <div class="map-container">
    <!-- マップヘッダー -->
    <div class="map-header" v-if="showHeader">
      <ContentText size="lg" color="primary" class="map-title">
        {{ title }}
      </ContentText>
      <div class="map-controls" v-if="showControls">
        <BaseButton
          @click="resetView"
          variant="secondary"
          size="sm"
          class="control-btn"
        >
          中央表示
        </BaseButton>
      </div>
    </div>

    <!-- マップ表示エリア -->
    <div
      ref="mapContainer"
      class="map-display"
    >
      <!-- マップが読み込まれるまでのローディング表示 -->
      <div v-if="!mapLoaded" class="map-loading">
        <ContentText>地図を読み込み中...</ContentText>
      </div>

      <!-- マップオーバーレイスロット（右下ボタン等） -->
      <div class="map-overlay">
        <slot name="overlay"></slot>
      </div>
    </div>

    <!-- マップフッター（座標表示等） -->
    <div class="map-footer" v-if="showFooter">
      <div class="coordinate-display">
        <ContentText size="sm" color="muted">
          緯度: {{ (currentCoordinate.lat || 0).toFixed(6) }},
          経度: {{ (currentCoordinate.lng || 0).toFixed(6) }}
        </ContentText>
      </div>
      <div class="zoom-display">
        <ContentText size="sm" color="muted">
          ズーム: {{ currentZoom }}
        </ContentText>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import maplibregl from 'maplibre-gl'
import { Auth, Signer } from 'aws-amplify'
import BaseButton from '../atoms/BaseButton.vue'
import ContentText from '../atoms/ContentText.vue'

/**
 * MapDisplay - 地図表示コンポーネント（Organismsレベルコンポーネント）
 *
 * アトミックデザインに準拠した地図表示専用のコンポーネントです。
 * MapLibre GLを使用して地図の表示、操作、マーカー管理などの地図関連機能を提供します。
 * 設計方針に従い、特定の業務機能（地図表示・位置情報管理）に特化したUIコンポーネントとして設計されています。
 *
 * アトミックデザイン分類: Organisms（有機体）
 * - MapLibre GLと複数のAtoms（BaseButton, ContentText）を組み合わせた地図特化型コンポーネント
 * - 地図表示という特定の業務機能に特化
 * - 地図操作、マーカー表示、座標管理を統合した複合的なUIコンポーネント
 *
 * 責務:
 * - 地図の初期化と表示
 * - 地図の操作制御（ズーム、パン、リセット）
 * - マーカーの表示・管理
 * - 現在座標とズームレベルの表示
 * - 全画面表示の切り替え
 * - 地図イベントの処理と親コンポーネントへの通知
 *
 * 外部インターフェース:
 * - Props: title, markers, center, zoom, mapStyle, showHeader, showFooter, showControls, enableMarkerDrag
 * - Events: mapClick, markerClick, viewChange, markerDragEnd
 * - Methods: addMarker(), removeMarker(), flyTo(), setZoom(), resetView(), refreshCredentials()
 *
 * 地図コントロール（showControls=trueの場合）:
 * - NavigationControl: ズーム・回転操作
 * - FullscreenControl: 全画面表示切り替え
 *
 * 地図スタイル:
 * - 'Honeycomb_Here_Explore': HERE Maps標準地図（デフォルト）
 * - 'Honeycomb_Esri_Satellite': Esri航空写真
 *
 * 使用例:
 * <MapDisplay
 *   title="施設位置マップ"
 *   :markers="locationMarkers"
 *   :center="[139.6917, 35.6895]"
 *   :zoom="10"
 *   :map-style="'Honeycomb_Here_Explore'"
 *   show-header
 *   show-footer
 *   show-controls
 *   @map-click="handleMapClick"
 *   @marker-click="handleMarkerClick"
 *   @view-change="handleViewChange"
 * />
 */

const props = defineProps({
  /**
   * マップのタイトル
   */
  title: {
    type: String,
    default: '地図表示'
  },

  /**
   * マーカーデータの配列
   * [{ id, lat, lng, title, description, icon? }] 形式
   */
  markers: {
    type: Array,
    default: () => []
  },

  /**
   * 地図の中心座標 [lng, lat] 形式
   */
  center: {
    type: Array,
    default: () => [139.6917, 35.6895] // 東京駅
  },

  /**
   * 初期ズームレベル
   */
  zoom: {
    type: Number,
    default: 10
  },

  /**
   * 地図スタイル
   * 'Honeycomb_Here_Explore' (標準地図) または 'Honeycomb_Esri_Satellite' (航空写真)
   */
  mapStyle: {
    type: String,
    default: 'Honeycomb_Here_Explore',
    validator: (value) => ['Honeycomb_Here_Explore', 'Honeycomb_Esri_Satellite'].includes(value)
  },

  /**
   * ヘッダー表示フラグ
   */
  showHeader: {
    type: Boolean,
    default: true
  },

  /**
   * フッター表示フラグ
   */
  showFooter: {
    type: Boolean,
    default: true
  },

  /**
   * コントロールボタン表示フラグ
   */
  showControls: {
    type: Boolean,
    default: true
  },

  /**
   * マーカーのドラッグ機能を有効にするフラグ
   */
  enableMarkerDrag: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['map-click', 'marker-click', 'marker-drag-end', 'center-view'])

// MapLibre GLインスタンス
const map = ref(null)
// マップ読み込み状態
const mapLoaded = ref(false)
// 現在の座標
const currentCoordinate = ref({
  lat: (props.center && props.center[1] != null) ? props.center[1] : 35.6895,
  lng: (props.center && props.center[0] != null) ? props.center[0] : 139.6917
})
// 現在のズームレベル
const currentZoom = ref(props.zoom)
// 初期の中心座標とズーム（リセット用）
const initialCenter = ref([...props.center])
const initialZoom = ref(props.zoom)
// マーカー インスタンスの管理
const markerInstances = ref(new Map())
// AWS認証情報
const credentials = ref(null)
// MapContainerの参照
const mapContainer = ref(null)

/**
 * マーカーデータの変更を監視
 */
watch(() => props.markers, (newMarkers) => {
  updateMarkers(newMarkers)
}, { deep: true })

/**
 * 中心座標の変更を監視
 */
watch(() => props.center, (newCenter) => {
  if (map.value) {
    flyTo(newCenter, currentZoom.value)
  }
})

/**
 * 地図を初期化します
 */
const initializeMap = () => {
  try {
    map.value = new maplibregl.Map({
      container: mapContainer.value,
      style: props.mapStyle, // AWS Location Serviceのスタイル名を直接指定
      center: props.center,
      zoom: props.zoom,
      transformRequest: transformRequest
    })

    // 地図のエラーハンドリング
    map.value.on('error', (e) => {
      // 色の解析エラーは無視する（警告のみ表示）
      if (e.error && e.error.message && e.error.message.includes('Could not parse color')) {
        console.warn('Map color parsing warning:', e.error.message)
        return
      }
      console.error('Map error:', e)
    })

    // 地図イベントの設定
    map.value.on('load', () => {
      mapLoaded.value = true
      updateMarkers(props.markers)
    })

    // NavigationControl（ズーム・回転コントロール）を追加
    if (props.showControls) {
      map.value.addControl(new maplibregl.NavigationControl(), 'top-right')
      map.value.addControl(new maplibregl.FullscreenControl(), 'top-right')
    }

    map.value.on('move', () => {
      const center = map.value.getCenter()
      currentCoordinate.value = {
        lat: center.lat,
        lng: center.lng
      }
      currentZoom.value = Math.round(map.value.getZoom() * 10) / 10
    })

    map.value.on('click', (e) => {
      emit('map-click', {
        lat: e.lngLat.lat,
        lng: e.lngLat.lng
      })
    })

  } catch (error) {
    console.error('地図の初期化に失敗しました:', error)
  }
}

/**
 * マーカーを更新します
 */
const updateMarkers = (markers) => {
  if (!map.value || !mapLoaded.value) return

  // 既存のマーカーをクリア
  markerInstances.value.forEach(marker => marker.remove())
  markerInstances.value.clear()

  // 新しいマーカーを追加
  markers.forEach(markerData => {
    addMarker(markerData)
  })
}

/**
 * マーカーを追加します
 */
const addMarker = (markerData) => {
  if (!map.value || !markerData.lat || !markerData.lng) return

  // マーカーオプションを明示的に設定
  const markerOptions = {
    draggable: props.enableMarkerDrag
  }

  // 色が指定されている場合のみ設定
  if (markerData.color && typeof markerData.color === 'string' && markerData.color.indexOf(';') === -1) {
    markerOptions.color = markerData.color
  }

  const marker = new maplibregl.Marker(markerOptions)
    .setLngLat([markerData.lng, markerData.lat])
    .addTo(map.value)

  // ドラッグ機能が有効な場合のイベントリスナー
  if (props.enableMarkerDrag) {
    let originalPosition = { lng: markerData.lng, lat: markerData.lat }

    marker.on('dragstart', () => {
      // ドラッグ開始時の位置を記録
      originalPosition = { ...marker.getLngLat() }
    })

    marker.on('dragend', () => {
      const newPosition = marker.getLngLat()

      // 位置変更イベントを発火
      emit('marker-drag-end', {
        id: markerData.id,
        originalPosition: originalPosition,
        newPosition: {
          lng: newPosition.lng,
          lat: newPosition.lat
        },
        markerData: markerData,
        marker: marker
      })
    })
  }

  // ポップアップの設定
  if (markerData.title || markerData.description) {
    const popup = new maplibregl.Popup({ offset: 25 })
      .setHTML(`
        <div class="marker-popup">
          <h6>${markerData.title || ''}</h6>
          <p>${markerData.description || ''}</p>
        </div>
      `)
    marker.setPopup(popup)
  }

  // マーカークリックイベント
  // MapLibreのMarkerはon('click')をサポートしていないため、
  // 外部ライブラリの制約上getElement().addEventListenerを使用
  marker.getElement().addEventListener('click', () => {
    emit('marker-click', markerData)
  })

  markerInstances.value.set(markerData.id, marker)
}

/**
 * マーカーを削除します
 */
const removeMarker = (markerId) => {
  const marker = markerInstances.value.get(markerId)
  if (marker) {
    marker.remove()
    markerInstances.value.delete(markerId)
  }
}

/**
 * 指定座標に移動します
 */
const flyTo = (center, zoom = null) => {
  if (map.value) {
    map.value.flyTo({
      center: center,
      zoom: zoom || currentZoom.value
    })
  }
}

/**
 * ズームレベルを設定します
 */
const setZoom = (zoom) => {
  if (map.value) {
    map.value.setZoom(zoom)
  }
}

/**
 * 地図をリセット（マーカーがある場合はマーカー位置を中心に、ない場合は初期位置に戻す）
 */
const resetView = () => {
  // マーカーがある場合は最初のマーカーの位置を中心にする
  if (props.markers && props.markers.length > 0) {
    const firstMarker = props.markers[0]
    const markerCenter = [firstMarker.lng, firstMarker.lat]
    const markerZoom = 16 // マーカー表示時は詳細ズームレベル
    flyTo(markerCenter, markerZoom)

    // 親コンポーネントに中央表示イベントを通知
    emit('center-view', {
      center: markerCenter,
      zoom: markerZoom,
      marker: firstMarker
    })
  } else {
    // マーカーがない場合は初期位置に戻す
    flyTo(initialCenter.value, initialZoom.value)

    // 親コンポーネントに中央表示イベントを通知
    emit('center-view', {
      center: initialCenter.value,
      zoom: initialZoom.value,
      marker: null
    })
  }
}

/**
 * AWS認証情報を更新します
 */
const refreshCredentials = async () => {
  try {
    credentials.value = await Auth.currentCredentials()
    // 認証情報の自動更新
    if (credentials.value && credentials.value.expiration) {
      setTimeout(refreshCredentials, credentials.value.expiration - new Date())
    }
  } catch (error) {
    console.error('AWS認証情報の取得に失敗しました:', error)
  }
}

/**
 * AWS Location Service用のtransformRequest
 */
const transformRequest = (url, resourceType) => {
  if (resourceType === 'Style' && !url.includes('://')) {
    // スタイル設定
    url = `https://maps.geo.ap-northeast-1.amazonaws.com/maps/v0/maps/${url}/style-descriptor`
  }
  if (url.includes('amazonaws.com')) {
    return {
      url: Signer.signUrl(url, {
        access_key: credentials.value.accessKeyId,
        secret_key: credentials.value.secretAccessKey,
        session_token: credentials.value.sessionToken,
      }),
    }
  }
  return { url }
}

onMounted(async () => {
  await refreshCredentials()
  initializeMap()
})

onBeforeUnmount(() => {
  if (map.value) {
    map.value.remove()
  }
})
</script>

<style lang="scss" scoped>
/* 地図コンテナ */
.map-container {
  display: flex;
  flex-direction: column;
  height: 100%; /* 親コンテナの高さに合わせる */
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #fff;
}

/* マップヘッダー */
.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;

  .map-title {
    margin: 0;
  }

  .map-controls {
    display: flex;
    gap: 0.5rem;
  }

  .control-btn {
    font-size: 0.875rem;
  }
}

/* マップ表示エリア */
.map-display {
  position: relative;
  flex: 1; /* フレックスアイテムとして残りのスペースを全て使用 */
  min-height: 300px; /* 最小高さのみ保持 */
}

/* ローディング表示 */
.map-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

/* マップオーバーレイ（右下ボタン等） */
.map-overlay {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  z-index: 1000;
  pointer-events: none;

  /* オーバーレイ内の要素はクリック可能に */
  > * {
    pointer-events: auto;
  }
}

/* マップフッター */
.map-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
  font-size: 0.875rem;
}

/* マーカーポップアップ */
:deep(.marker-popup) {
  h6 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: #6c757d;
  }
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .map-header {
    padding: 0.75rem;
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;

    .map-controls {
      justify-content: center;
    }
  }

  .map-display {
    height: 300px;
    min-height: 250px;
  }

  .map-footer {
    padding: 0.5rem 0.75rem;
    flex-direction: column;
    gap: 0.25rem;
    text-align: center;
  }

  .control-btn {
    flex: 1;
  }
}

@media (max-width: 576px) {
  .map-display {
    height: 250px;
    min-height: 200px;
  }
}
</style>
